import React, { useMemo } from "react";
import { AlertTriangle, CheckCircle, DollarSign, Users } from "lucide-react";

const StepReviewUnpay = ({
  selectedEmployees = [],
  modalStyles,
  currentTheme,
  t,
}) => {
  const totalNetSalary = useMemo(
    () => selectedEmployees.reduce((sum, emp) => sum + (emp.netSalary || 0), 0),
    [selectedEmployees],
  );

  const groupedByDepartment = useMemo(() => {
    return selectedEmployees.reduce((acc, employee) => {
      const dept = employee.department || "Unknown";
      if (!acc[dept]) acc[dept] = [];
      acc[dept].push(employee);
      return acc;
    }, {});
  }, [selectedEmployees]);

  return (
    <div style={{ padding: "0 24px 24px" }}>
      {/* Warning */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "16px",
          background: `${currentTheme.colors.danger}10`,
          border: `1px solid ${currentTheme.colors.danger}30`,
          borderRadius: "10px",
          marginBottom: "24px",
        }}
      >
        <AlertTriangle size={20} color={currentTheme.colors.danger} />
        <div>
          <div
            style={{
              fontWeight: "700",
              color: currentTheme.colors.danger,
              fontSize: "14px",
            }}
          >
            {t("importantConfirmUnpay")}
          </div>
          <div
            style={{
              fontSize: "13px",
              color: currentTheme.colors.textSecondary,
              marginTop: "4px",
            }}
          >
            {t("unpayConfirmationDesc")}
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            padding: "16px",
            background: currentTheme.colors.cardBackground,
            border: `1px solid ${currentTheme.colors.borderLight}`,
            borderRadius: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "8px",
            }}
          >
            <Users
              size={18}
              color={currentTheme.colors.primary}
            />
            <span
              style={{
                fontSize: "13px",
                color: currentTheme.colors.textSecondary,
              }}
            >
              {t("employees")}
            </span>
          </div>
          <div
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: currentTheme.colors.textPrimary,
            }}
          >
            {selectedEmployees.length}
          </div>
        </div>

        <div
          style={{
            padding: "16px",
            background: currentTheme.colors.cardBackground,
            border: `1px solid ${currentTheme.colors.borderLight}`,
            borderRadius: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "8px",
            }}
          >
            <DollarSign
              size={18}
              color={currentTheme.colors.danger}
            />
            <span
              style={{
                fontSize: "13px",
                color: currentTheme.colors.textSecondary,
              }}
            >
              {t("totalNetSalary")}
            </span>
          </div>
          <div
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: currentTheme.colors.danger,
            }}
          >
            {totalNetSalary.toLocaleString()} {t("currency")}
          </div>
        </div>
      </div>

      {/* Employee List by Department */}
      <div
        style={{
          padding: "16px",
          background: currentTheme.colors.cardBackground,
          border: `1px solid ${currentTheme.colors.borderLight}`,
          borderRadius: "10px",
        }}
      >
        <div
          style={{
            fontSize: "15px",
            fontWeight: "700",
            color: currentTheme.colors.textPrimary,
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <CheckCircle size={18} color={currentTheme.colors.success} />
          {t("selectedEmployees")}
        </div>

        <div style={{ maxHeight: "300px", overflowY: "auto" }}>
          {Object.entries(groupedByDepartment)
            .sort()
            .map(([dept, employees]) => (
              <div key={dept} style={{ marginBottom: "16px" }}>
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: "700",
                    color: currentTheme.colors.textSecondary,
                    marginBottom: "8px",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span>{dept}</span>
                  <span>({employees.length})</span>
                </div>
                {employees.map((emp) => (
                  <div
                    key={emp.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      background: `${currentTheme.colors.danger}08`,
                      borderRadius: "6px",
                      marginBottom: "4px",
                      fontSize: "13px",
                    }}
                  >
                    <span style={{ color: currentTheme.colors.textPrimary }}>
                      {emp.employeeName}
                    </span>
                    <span style={{ color: currentTheme.colors.danger, fontWeight: "600" }}>
                      {emp.netSalary.toLocaleString()} {t("currency")}
                    </span>
                  </div>
                ))}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default StepReviewUnpay;
